import { PROJECT } from '../project'
import { PRODUCT_NAME } from '../pages'

export type FeedbackCategory = 'bug' | 'feature' | 'data' | 'general'

/** What the issue says about the session, field for field, when the person agrees. */
export interface FeedbackContext {
  route: string
  reportCount: number
  softwareCount: number
  /** Sources followed, by name. */
  sources: string[]
  theme: string
  screenSize: string
  browser: string
  timestamp: string
}

export interface Feedback {
  category: FeedbackCategory
  message: string
  context?: FeedbackContext
}

export const GITHUB_ISSUES_URL = `${PROJECT.issuesUrl}/new`

const CATEGORY_NAMES: Record<FeedbackCategory, string> = {
  bug: 'Bug report',
  feature: 'Feature suggestion',
  data: 'Indicator or data issue',
  general: 'General feedback',
}

const CATEGORY_TAGS: Record<FeedbackCategory, string> = {
  bug: '[Bug]: ',
  feature: '[Feature]: ',
  data: '[Data]: ',
  general: '[Feedback]: ',
}

/** The feedback as a Markdown issue body. */
export function buildFeedbackMarkdown(feedback: Feedback): string {
  const parts = [
    '### Category',
    CATEGORY_NAMES[feedback.category],
    '',
    '### Details',
    feedback.message.trim(),
    '',
  ]
  if (feedback.context) {
    parts.push(
      '### Diagnostic context',
      '```json',
      JSON.stringify(feedback.context, null, 2),
      '```',
      '',
    )
  }
  parts.push('---', `*Submitted from ${PRODUCT_NAME} ${PROJECT.version}, in-app feedback dialog*`)
  return parts.join('\n')
}

/** Longer addresses are refused or cut short by GitHub and by browsers. */
export const MAX_ISSUE_URL = 8000
const SHORTENED =
  '\n\n*Shortened to fit in a link: paste the full report, copied from the dialog, here.*'

/**
 * A link that opens a new GitHub issue filled in with the feedback, titled by
 * the first line of the message. A long report is shortened to fit, with a
 * note asking for the full copy.
 */
export function buildGitHubIssueUrl(given: Feedback): string {
  // Half of a pasted emoji is text encodeURIComponent throws on, and this runs
  // while the dialog renders; it becomes the replacement character instead.
  const feedback = { ...given, message: given.message.replace(/\p{Surrogate}/gu, '�') }
  const firstLine = feedback.message.trim().split('\n')[0] ?? ''
  const title = `${CATEGORY_TAGS[feedback.category]}${cut(firstLine, 60)}`
  const urlFor = (body: string) =>
    `${GITHUB_ISSUES_URL}?title=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`
  const body = buildFeedbackMarkdown(feedback)
  let url = urlFor(body)
  for (let keep = [...body].length; url.length > MAX_ISSUE_URL && keep > 0;) {
    keep = Math.floor(keep * 0.8)
    url = urlFor(cut(body, keep) + SHORTENED)
  }
  return url
}

/**
 * The first `characters` of a text, counted as characters rather than UTF-16
 * units: cutting an emoji in half leaves text encodeURIComponent throws on.
 */
function cut(text: string, characters: number): string {
  return [...text].slice(0, characters).join('')
}
